package it.maicol07.gamerlogue.data

import gamerlogue.sharedui.generated.resources.Res
import gamerlogue.sharedui.generated.resources.library__completion_100
import gamerlogue.sharedui.generated.resources.library__completion_main_plus_sides
import gamerlogue.sharedui.generated.resources.library__completion_main_story
import it.maicol07.gamerlogue.ui.views.library.GameLibraryStatus
import it.maicol07.spraypaintkt_annotation.Attr
import it.maicol07.spraypaintkt_annotation.Relation
import it.maicol07.spraypaintkt_annotation.ResourceSchema
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.StringResource

/** The server's rating range: every rating is sent and stored on 0–[MAX_RATING], whatever scale is shown. */
const val MAX_RATING = 100

@ResourceSchema(endpoint = "library_entries", resourceType = "LibraryEntry")
interface LibraryEntrySchema {
    @Attr val gameId: Int
    @Attr val status: GameLibraryStatus
    @Attr val completionStatus: CompletionStatus?
    @Attr val owned: Boolean
    @Attr val editionsIds: List<Int>
    @Attr val platformsIds: List<Int>
    @Attr val startDate: String?
    @Attr val endDate: String?
    @Attr val playedTime: Int? // in hours
    @Attr val rating: Number? // 0-MAX_RATING

    /**
     * A detailed review's sections as `{key: {rating, review}}`; read them through [reviewSections].
     * Untyped because the server sends an empty one as a JSON array (`[]`).
     */
    @Attr val ratingDetails: Any?
    @Attr val review: String?
    @Attr val reviewSpoiler: Boolean
        get() = false

    /** Changes only when the review text does. */
    @Attr val reviewUpdatedAt: String?

    /** The owner's UUID; read-only, the server assigns it. */
    @Attr val userId: String?
    @Attr val likesCount: Int
        get() = 0
    @Attr val commentsCount: Int
        get() = 0
    @Attr val likedByMe: Boolean
        get() = false
    @Attr val createdAt: String?
    @Attr val updatedAt: String?
    @Relation val user: UserSchema?

    /** A text, sections or just a rating, as the server's `filter[has_review]` and `review_required` count it. */
    val hasReview: Boolean
        get() = !review.isNullOrBlank() || reviewSections.isNotEmpty() || rating != null

    /** [ratingDetails] narrowed defensively: an array or a value in any other shape yields no section. */
    val reviewSections: List<ReviewSection>
        get() = (ratingDetails as? Map<*, *>).orEmpty().mapNotNull { (key, value) ->
            if (key !is String || value !is Map<*, *>) return@mapNotNull null
            ReviewSection(key, (value["rating"] as? Number)?.toDouble(), value["review"] as? String ?: "")
        }

    enum class CompletionStatus(val displayName: StringResource) {
        MAIN_STORY(Res.string.library__completion_main_story),
        MAIN_PLUS_SIDES(Res.string.library__completion_main_plus_sides),
        FULL_100(Res.string.library__completion_100)
    }

    val startLocalDate: LocalDate?
        get() = startDate?.let(::parseCalendarDate)
    val endLocalDate: LocalDate?
        get() = endDate?.let(::parseCalendarDate)
}

/**
 * The calendar day of a date attribute. The backend sends date columns with a time and offset
 * (`2026-01-01T00:00:00+01:00`): only the date part is meaningful, and converting the whole value
 * to UTC would land on the day before.
 */
private fun parseCalendarDate(value: String): LocalDate? = LocalDate.Formats.ISO.parseOrNull(value.substringBefore('T'))
